// Copyright 2026, Auxilio Technologies LLC

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dashboardAPI, DashboardSummary } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { hasPermission, RoleType, Permissions } from '../config/rolesConfig';
import { getLocalDateString } from '../lib/orderUtils';

type Period = 'today' | '7d' | '30d' | 'all';

function rangeFor(period: Period): { from?: string; to?: string } {
  if (period === 'all') return {};
  const to = new Date();
  const from = new Date();
  if (period === '7d') from.setDate(from.getDate() - 6);
  if (period === '30d') from.setDate(from.getDate() - 29);
  return { from: getLocalDateString(from), to: getLocalDateString(to) };
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const role = (user?.role || 'viewer') as RoleType;
  const canSeeStats = hasPermission(role, Permissions.ORDERS_READ) || hasPermission(role, Permissions.DISPATCH_READ);

  const [period, setPeriod] = useState<Period>('today');
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canSeeStats) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { from, to } = rangeFor(period);
    dashboardAPI
      .summary(from, to)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period, canSeeStats]);

  const p = data?.pipeline || {};
  const oc = data?.outcomes || {};
  const val = (n?: number) => (loading ? '—' : n ?? 0);

  // Pipeline cards (where an order is right now).
  const pipelineCards = [
    { key: 'newOrders', value: p['new'], color: 'text-gray-600 dark:text-gray-300' },
    { key: 'inPrep', value: p['in_prep'], color: 'text-amber-600' },
    { key: 'ready', value: p['ready'], color: 'text-blue-600' },
    { key: 'outForDelivery', value: p['out_for_delivery'], color: 'text-purple-600' },
  ];

  // Delivery outcome cards (what happened at the door). Delivered combines hand-off + safe drops;
  // Returned to Shop folds in legacy "not home" (which meant the order came back).
  const deliveredTotal = (oc['delivered'] || 0) + (oc['left_with_neighbour'] || 0) + (oc['safe_place'] || 0);
  const returnedTotal = (oc['returned'] || 0) + (oc['not_home'] || 0) + (oc['wrong_address'] || 0) + (oc['refused'] || 0);
  const outcomeCards = [
    { key: 'outcomeDelivered', value: deliveredTotal, color: 'text-green-600' },
    { key: 'leftWithNeighbour', value: oc['left_with_neighbour'], color: 'text-blue-600' },
    { key: 'leftAtDoor', value: oc['safe_place'], color: 'text-amber-600' },
    { key: 'returnedToShop', value: returnedTotal, color: 'text-red-600' },
  ];
  const anyOutcomes = useMemo(() => Object.values(oc).some((v) => (v || 0) > 0), [oc]);

  const periods: Period[] = ['today', '7d', '30d', 'all'];
  const periodLabel: Record<Period, string> = { today: 'today', '7d': 'last7', '30d': 'last30', all: 'allTime' };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('dashboard.title')}</h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">{t('dashboard.subtitle')}</p>
        </div>
        {canSeeStats && (
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800">
            {periods.map((per) => (
              <button
                key={per}
                onClick={() => setPeriod(per)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                  period === per ? 'bg-rose-600 text-white' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {t(`dashboard.${periodLabel[per]}`)}
              </button>
            ))}
          </div>
        )}
      </div>

      {canSeeStats && (
        <>
          {/* Total */}
          <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="text-4xl font-bold text-rose-600">{val(data?.total)}</div>
            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('dashboard.totalOrders')}</div>
          </div>

          {/* Pipeline */}
          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-400">{t('dashboard.pipeline')}</h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {pipelineCards.map((c) => (
                <div key={c.key} className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                  <div className={`text-3xl font-bold ${c.color}`}>{val(c.value)}</div>
                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t(`dashboard.${c.key}`)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Delivery outcomes */}
          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-400">{t('dashboard.outcomes')}</h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              {outcomeCards.map((c) => (
                <div key={c.key} className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                  <div className={`text-3xl font-bold ${c.color}`}>{val(c.value)}</div>
                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t(`dashboard.${c.key}`)}</div>
                </div>
              ))}
            </div>
            {!loading && !anyOutcomes && (
              <p className="mt-3 text-sm text-gray-400 dark:text-gray-500">{t('dashboard.noOutcomes')}</p>
            )}
          </div>
        </>
      )}

      {/* Quick links */}
      <div className="flex flex-wrap gap-3 pt-2">
        {hasPermission(role, Permissions.ORDERS_READ) && (
          <button onClick={() => navigate('/orders')} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700">
            {t('dashboard.goToBoard')}
          </button>
        )}
        {hasPermission(role, Permissions.PREP_READ) && (
          <button onClick={() => navigate('/prep')} className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
            {t('dashboard.goToPrep')}
          </button>
        )}
        {hasPermission(role, Permissions.DRIVER_READ) && (
          <button onClick={() => navigate('/driver')} className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
            {t('dashboard.goToDriver')}
          </button>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
